import {Component, ElementRef, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromStore from "../store";
import {AuthService} from "../auth/auth.service";
import {combineLatest, Observable} from "rxjs";
import {filter} from "rxjs/operators";
import {first, switchMap, takeUntil} from "rxjs/operators";
import {
  UserSubscription,
  UserSubscriptionCategory,
  UserSubscriptionIds,
  UserSubscriptionType
} from "../user/user-subscription";
import {clientRoles} from "../user/user-roles-actions";
import {UserManagementService} from "../user-managment/user-management.service";
import {Subject} from "rxjs";
import {Workspace} from "../user-managment/workspace";
import {Router} from "@angular/router";
import {GlobalEvent} from "../shared/services/global-event-dispatcher/global-event";
import {GlobalEventDispatcherService} from "../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {UserService} from "../user/user.service";
import {User} from "../user/user";
import {UserSubscriptionStatus} from "../user/user-subscription-status";
import {TextUtils} from "../shared/utils/text-utils";
import {TypeFormAnswers} from "../add-website/typeFormAnswers";
import {DbResponse} from "../shared/interceptors/db-response";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";
import {DialogService} from "../../ng2-bootstrap-modal/dialog.service";
import {Config} from "../config/config";
import {CookieService} from "ngx-cookie";
import {DbModalService} from "../modules/modal/modal";
import {Utils} from "../shared/utils/utils";
import {TypeUtils} from "../shared/utils/type-utils";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";

@Component({
    selector: 'db-workspace-popup',
    templateUrl: './workspace-popup.component.html',
    styleUrls: ['./workspace-popup.component.scss'],
    standalone: false
})

export class WorkspacePopupComponent implements OnInit, OnDestroy {

  public Config = Config;
  public showSwitch: boolean = false;
  public workspaceRoles = clientRoles;
  public user: User;
  public workspace: Workspace;
  public workspaces: { [key: string]: Workspace } = {};
  public ownerWorkspaceId: number;
  public currentWorkspaceId: number;
  public isChecked: boolean = false;
  public openedKnowledgeBase: boolean;
  public toggledKnowledgeBase$: Observable<boolean>;
  public userSubscription: UserSubscription;
  public UserSubscriptionCategory: typeof UserSubscriptionCategory = UserSubscriptionCategory;
  public userSubscriptionType = UserSubscriptionType;
  public userSubscriptionIds = UserSubscriptionIds;
  public userSubscriptionStatus = UserSubscriptionStatus;
  public isOwnWorkspaceActive: boolean;
  public userLocalStorage: any;
  public hasUpgrade: boolean;
  public showWorkspaceTour: boolean = false;
  public showWorkspaceSwitchTour: boolean = false;
  public showActivityLogTour: boolean = false;
  public isMobile: boolean;
  public firstTimePopupTitle: string;
  public firstTimePopupDesc: string;
  public firstTimePopupButton1: string;
  public firstTimePopupButton2?: string;
  public hasCloseButton: boolean;
  public trialUpgradable: boolean;
  public typeFormAnswers = TypeFormAnswers;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  private userRoleId: number;
  private redirect: any;
  public expiredSubscr: boolean;
  public dbTesting: boolean;
  public demoUser: boolean;

  constructor(
    private $element: ElementRef,
    private _store: Store<fromStore.DbStoreState>,
    private _authService: AuthService,
    private _userManagementService: UserManagementService,
    private _router: Router,
    private _userService: UserService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _dialogService: DialogService,
    private _cookieService: CookieService,
    private _modalService: DbModalService,
  ) {
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 991;
    this.dbTesting = !!this._cookieService.get('db-testing');

    this.toggledKnowledgeBase$ = this._store.select(fromStore.getKnowledgeBaseToggled);
    this.toggledKnowledgeBase$
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((isOpened: any) => {
        this.openedKnowledgeBase = isOpened;
      });
    this._store.select(fromStore.getCurrentWorkspace)
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(currentWorkspace => {
        this.userRoleId = currentWorkspace && currentWorkspace.roleId;
      });
    combineLatest(
      this._store.select(fromStore.getUserLoaded),
      this._store.select(fromStore.getUserLocalStorageLoaded)
    )
      .pipe(
        filter(([userLoaded, userLocalStorageLoaded]) => !!userLoaded && !!userLocalStorageLoaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getWorkspaces),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getUserUpgradableSubscription()),
          this._store.select(fromStore.getUserAvailableHostingSubscriptions()),
          this._store.select(fromStore.getWorkspaceIdEntities),
          this._store.select(fromStore.getOwnerWorkspace),
          this._store.select(fromStore.getUserLocalStorage)
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, workspaces, userSubscription, upgradableSubscriptions, availableHostingSubscriptions, workspaceIds, ownWorkspace, localStorage]) => {
        workspaces.forEach(workspace => {
          if (workspace && workspace.status !== 'invited') {
            this.workspaces[workspace.workspaceId] = workspace;
          }
        });
        this.showSwitch = Object.values(this.workspaces).length > 1;
        this.ownerWorkspaceId = workspaceIds.ownerWorkspaceId;
        this.currentWorkspaceId = workspaceIds.currentWorkspaceId || this.ownerWorkspaceId;
        this.user = user;
        this.userSubscription = userSubscription;
        this.trialUpgradable = userSubscription && userSubscription.trialBoosterClient;
        this.expiredSubscr = this.userSubscription && ([this.userSubscriptionStatus.failed, this.userSubscriptionStatus.expired].includes(this.userSubscription.status) || this.userSubscription.isExpired);
        this.userLocalStorage = localStorage;
        this.hasUpgrade = !!upgradableSubscriptions && !((availableHostingSubscriptions.length === 1 && !this.userSubscription?.isApiResellerPlan) && availableHostingSubscriptions.filter((_userSubscription => _userSubscription?.tabCategory === this.userSubscription?.tabCategory)));
        this.isOwnWorkspaceActive = ownWorkspace && ownWorkspace.status !== 'invited';
        this.demoUser = Utils.isDemoUser(userSubscription, user);
        this.changeSelectedWorkspace();
      });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }


  public gotIt() {
    this.userLocalStorage['hideWorkspaceInviteTour'] = true;
    this.closeFirstTimePopUp("hideWorkspaceInviteTour");
    this.setLocalStorage();
  }

  public gotItActivityLog() {
    this.userLocalStorage['hideActivityLogTour'] = true;
    this.closeFirstTimeActivityLogPopUp("hideActivityLogTour");
    this.setLocalStorage();
  }

  public popupVisibility(): void {
    this.userLocalStorage['popupVisibility'] = true;
    this.setLocalStorage();
  }

  public setLocalStorage(): void {
    this._userService.setToLocal(this.userLocalStorage)
    .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }

  public sendToAnalytics(action): void {
    const analyticsData = {
      eventCategory: 'Client Management',
      eventAction: action,
      eventLabel: 'Popup Visibility'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public switchWorkspace(workspace): void {

    this.hidePopup();
    this.redirect = true;
    this._store.dispatch(new fromStore.SwitchWorkspace({
      switching: true,
      workspaceName: workspace.workspaceName,
      redirect: true
    }));

    this._userManagementService.switchWorkspace(workspace?.workspaceId)
      .pipe(
        filter(response => !!response),
        first()
      )
      .subscribe(response => {
        if (response.success) {
          // this.changeSelectedWorkspace(workspace.workspaceId);
          if (this.redirect) {
            this._router.navigate(['/websites']);
            this._globalEventDispatcher.dispatchEvent(GlobalEvent.workspaceSwitch);
          }
        } else {
          this._store.dispatch(new fromStore.SwitchWorkspace({switching: false, workspaceName: '', redirect: true}));
        }
      });
  }

  private changeSelectedWorkspace(workspaceId?: number): void {
    this.workspace = workspaceId ? this.workspaces[workspaceId] : this.workspaces[this.currentWorkspaceId];
    if (this.workspace) {
      delete this.workspaces[this.workspace.workspaceId];
    }
  }

  public addOwnWorkspace() {
    if (!localStorage.getItem('dbPreviousWorkspaceId') || !localStorage.getItem('dbPreviousWorkspaceRoleId')) {
      localStorage.setItem('dbPreviousWorkspaceId', localStorage.getItem('dbCurrentWorkspaceId'));
      localStorage.setItem('dbPreviousWorkspaceRoleId', localStorage.getItem('dbCurrentWorkspaceRoleId'));
    }
    const switchToWorkspace = {
      ownerWorkspaceId: localStorage.getItem('dbOwnWorkspaceId'),
      currentWorkspaceId: localStorage.getItem('dbOwnWorkspaceId'),
      currentWorkspaceRoleId: 1
    };
    this._store.dispatch(new fromStore.SwitchWorkspace({switching: true, workspaceName: '', redirect: false}));

    if (this.userRoleId !== clientRoles.OWNER) {
      this._userManagementService.switchWorkspace(switchToWorkspace.currentWorkspaceId)
        .pipe(
          filter(response => !!response),
          first()
        )
        .subscribe(response => {
          if (response.success) {
            this._globalEventDispatcher.dispatchEvent(GlobalEvent.workspaceSwitch);
            this._router.navigate(['/upgrade-plan'], {queryParams: {currentUser: true}});
            this._store.dispatch(new fromStore.SwitchWorkspace({switching: true, workspaceName: '', redirect: false}));
          }
        });
    }
  }

  public hidePopup(routerLink?): void {
    this.isChecked = false;
    if (routerLink) {
      if (this.demoUser) {
        this._openCheckoutDialog();
      } else {
        this._router.navigate([routerLink]);
      }
    }
  }

  private _openCheckoutDialog() {
    const analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: 'Locked button click',
      eventLabel: 'Workspace info'
    };
    const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
    const options = {
      overflowHidden: false,
    };
    if (!firstTime) {
      options['width'] = 1240;
    } else {
      options['width'] = 460;
      options['height'] = 600;
    }
    const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
    modal.componentInstance.showFirstTime = firstTime;
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    return false;
  }

  public closeFirstTimePopUp(event): void {
    event === 'hideWorkspaceSwitchTour' ? this.showWorkspaceSwitchTour = false : this.showWorkspaceTour = false;
    this.isChecked = false;
  }

  public closeFirstTimeActivityLogPopUp(event): void {
    event === 'hideActivityLogTour' ? this.showActivityLogTour = false : this.showActivityLogTour = false;
    this.isChecked = false;
  }

  public sendFreeUpgradeEvent(): void {
    if (this.userSubscription.type !== UserSubscriptionType.FREE) {
      return;
    }
    let analyticsData = {
      eventCategory: this.userSubscription && this.userSubscription.trialBoosterClient ? 'Dashboard Action' : 'Free Upgrade Offer',
      eventAction: "Upgrade page view",
      eventLabel: this.userSubscription && this.userSubscription.trialBoosterClient ? "Speed Booster: Workspace Tooltip" : "Workspace Tooltip"
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    this.hidePopup();
  }

  public changeValue($event): void {
    this.isChecked = $event.target.checked;
    this.openedKnowledgeBase && this._store.dispatch(new fromStore.ToggleKnowledgeBase());
  }

  @HostListener('document:click', ['$event']) onDocumentClick($event: any) {
    !this.isMobile && (this.showWorkspaceTour || this.showWorkspaceSwitchTour || this.showActivityLogTour) || this.$element.nativeElement.contains($event.target) ? $event.stopImmediatePropagation() : this.isChecked = false;
  }
}
